import type {
  AccountBindingChallengeRequest,
  AccountBindingChallengeResponse,
  AccountBindingConfirmRequest,
  AccountBindingConfirmResponse,
  AccountDailyMetricsResponse,
  AccountDiagnosisResponse,
  AccountSyncRun,
  ManualSelfAnalysisRequest,
  AccountsStatusResponse,
  ContentDraftArchiveResponse,
  ContentDraftDeleteResponse,
  ContentDraftPinRequest,
  ContentDraftPinResponse,
  ContentDraftReadResponse,
  ContentDraftStatus,
  ContentDraftUpdateRequest,
  ContentDraftUpdateResponse,
  ContentDraftVersionReadResponse,
  RewriteContentDraftRequest,
  RewriteContentDraftResponse,
  CompetitorInsightDetailResponse,
  CompetitorInsightRequest,
  CompetitorInsightRequestResponse,
  DailyRecommendationFeedbackRequest,
  DailyRecommendationResponse,
  ContentDraftsListResponse,
  CreatorsListResponse,
  GenerateDraftRequest,
  GenerateDraftResponse,
  GenerateDraftVariantsRequest,
  GenerateDraftVariantsResponse,
  ContentGenerationBatchSubmitRequest,
  ContentGenerationBatchResponse,
  ContentDraftGenerationBatchReadResponse,
  AiPointLedgerQueryInput,
  AiPointLedgerResponse,
  AiChatCompletionRequest,
  AiChatCompletionResponse,
  AiChatGenerationAcceptedResponse,
  AiChatStreamEvent,
  AiChatQuoteRequest,
  AiChatQuoteResponse,
  AiChatAttachmentCreateRequest,
  AiChatAttachmentCreateResponse,
  AiChatAttachmentDeleteResponse,
  AiChatAttachmentUploadResponse,
  AiChatConversation,
  AiChatConversationDeleteResponse,
  AiChatConversationListResponse,
  AiChatConversationRenameRequest,
  AiChatMessageListResponse,
  IngestPlatform,
  NoteCrawlPurpose,
  NoteDetailResponse,
  NotesListResponse,
  Platform,
  PlatformCatalogResponse,
  PlanCatalogResponse,
  SavedTopicCreateRequest,
  SavedTopicCreateResponse,
  SavedTopicDeleteResponse,
  SavedTopicsListResponse,
  SettingsResponse,
  SettingsUpdateRequest,
  SkillArchiveResponse,
  SkillCreateRequest,
  SkillCreateResponse,
  SkillExtractionReadinessResponse,
  SkillExtractionStartRequest,
  SkillExtractionStartResponse,
  SkillManageResponse,
  SkillReadResponse,
  SkillSelectRequest,
  SkillSelectResponse,
  SkillSourceType,
  SkillsListResponse,
  SkillUpdateRequest,
  SkillUpdateResponse,
  UserAiModelsResponse,
  AdminNotificationPreviewResponse,
  AdminNotificationPublishRequest,
  AdminChatConfigCacheRebuildRequest,
  AdminChatConfigDraft,
  AdminChatConfigDraftUpdateRequest,
  AdminChatConfigPublishRequest,
  AdminChatConfigRepublishRequest,
  AdminChatConfigSkillArchiveRequest,
  AdminChatConfigSkillOrderRequest,
  AdminChatConfigSkillStatusRequest,
  AdminChatConfigVersionListQuery,
  AdminChatSkillArchiveResponse,
  AdminChatSkillCreateRequest,
  AdminChatSkillDetailResponse,
  AdminChatSkillsListResponse,
  AdminChatSkillSummary,
  AdminChatSkillUpdateRequest,
  ChatConfigPublicationOperationKind,
  ChatConfigPublicationOperationStatus,
  ChatConfigSnapshot,
  NotificationListResponse,
  NotificationPublicationsResponse,
  NotificationReadResponse,
  NotificationTarget,
  NotificationUnreadCountResponse,
} from '@ignite/types';
import {
  ApiErrorBody,
  AiChatAttachmentCreateResponseSchema,
  AiChatAttachmentDeleteResponseSchema,
  AiChatAttachmentUploadResponseSchema,
  AiChatCompletionResponseSchema,
  AiChatConversationDeleteResponseSchema,
  AiChatConversationListResponseSchema,
  AiChatConversationSchema,
  ExtensionConnectCodeResponse,
  PublicPlansResponse,
  UserLogoutResponse,
  ContentGenerationBatchResponseBody,
  LatestContentGenerationBatchResponseBody,
  ContentDraftGenerationBatchReadResponseBody,
  AiChatGenerationAcceptedResponseSchema,
  AiChatCursorConflictDetailsSchema,
  AiChatMessageListResponseSchema,
  AiChatQuoteResponseSchema,
  AI_CHAT_STREAM_CLIENT_MAX_BYTES,
  AiChatStreamEventSchema,
} from '@ignite/types';
import { z, type ZodType } from 'zod';

export interface ApiClientOptions {
  baseUrl: string;
  getToken?: () => string | null | Promise<string | null>;
  onUnauthorized?: (error: ApiError) => void | Promise<void>;
}

export interface AuthRequest {
  email: string;
  password: string;
}

export interface VerifyEmailRequest {
  email: string;
  code: string;
}

export interface PasswordResetConfirmRequest {
  email: string;
  code: string;
  password: string;
}

export interface ResendCodeRequest {
  email: string;
}

export interface AuthUser {
  id: string;
  email: string;
}

export interface AuthSessionResponse {
  token: string;
  user: AuthUser;
  tenant_id?: string;
}

export interface AuthVerificationResponse {
  user: AuthUser;
  verification_required: true;
  code_expires_in: number;
}

export type AuthResponse = AuthSessionResponse | AuthVerificationResponse;

export interface ResendCodeResponse {
  ok: true;
  code_expires_in?: number;
  retry_after?: number;
}

export interface PublicConfigResponse {
  registration_enabled: boolean;
}

export interface PluginFailureEvent {
  id: string;
  code: string;
  occurred_at: string;
  detail?: string;
}

export interface PluginStatusResponse {
  selector_version: string;
  crawls_last_7_days: number;
  failures: PluginFailureEvent[];
}

export interface ListNotesOptions {
  platform?: IngestPlatform;
  accountId?: string;
  crawlPurpose?: NoteCrawlPurpose;
  createdAfter?: string;
  createdBefore?: string;
  cursor?: string;
  limit?: number;
}

export interface ListCreatorsOptions {
  platform?: IngestPlatform;
  cursor?: string;
  limit?: number;
}

export interface AccountDailyMetricsOptions {
  from?: string;
  to?: string;
}

export interface ListSkillsOptions {
  platform: Platform;
  layer?: number;
  sourceType?: SkillSourceType;
  category?: string;
  limit?: number;
}

export interface ListContentDraftsOptions {
  platform?: Platform;
  q?: string;
  status?: ContentDraftStatus;
  cursor?: string;
  limit?: number;
}

export interface ListSavedTopicsOptions {
  platform?: Platform;
  cursor?: string;
  limit?: number;
}

export interface ReadLatestContentGenerationBatchOptions {
  platform: Platform;
}

export interface ListNotificationsOptions {
  status?: 'all' | 'unread';
  cursor?: string;
  limit?: number;
}

export interface ListNotificationPublicationsOptions {
  cursor?: string;
  limit?: number;
}

export interface ListAdminChatConfigSkillsOptions {
  cursor?: string;
  limit?: number;
  q?: string;
  status?: AdminChatSkillSummary['status'];
}

export interface ListAdminChatConfigVersionsOptions {
  limit?: AdminChatConfigVersionListQuery['limit'];
}

export interface AdminChatConfigOperation {
  operation_id: string;
  client_request_id: string;
  operation_kind: ChatConfigPublicationOperationKind;
  target_version_id: string;
  expected_current_version: string | null;
  expected_draft_revision: number | null;
  status: ChatConfigPublicationOperationStatus;
  error_code: string | null;
  created_at: string;
  committed_at: string | null;
  completed_at: string | null;
}

export interface AdminChatConfigVersionSummary {
  version_id: string;
  draft_revision: number;
  snapshot_schema_version: number;
  snapshot_sha256: string;
  selector_model_id: string;
  created_by: string;
  created_at: string;
  published_at: string;
  is_current: boolean;
}

export interface AdminChatConfigRefreshSummary {
  operation_id: string;
  operation_kind: ChatConfigPublicationOperationKind;
  target_version_id: string;
  status: ChatConfigPublicationOperationStatus;
  error_code: string | null;
  updated_at: string;
  completed_at: string | null;
}

export interface AdminChatConfigCacheInspection {
  authoritative_version_id: string | null;
  kv_version_id: string | null;
  consistent: boolean;
  cache_status:
    | 'valid'
    | 'missing'
    | 'missing_version'
    | 'invalid'
    | 'read_error'
    | 'stale'
    | 'unpublished';
  last_checked_at: string;
  last_refresh: AdminChatConfigRefreshSummary | null;
}

export type AdminChatConfigDraftResponse = { draft: AdminChatConfigDraft };
export type AdminChatConfigSkillMutationResponse = {
  draft_revision: number;
  skill: AdminChatSkillSummary;
};
export type AdminChatConfigSkillArchiveMutationResponse = AdminChatSkillArchiveResponse & {
  draft_revision: number;
};
export type AdminChatConfigOperationResponse = { operation: AdminChatConfigOperation };
export type AdminChatConfigVersionsResponse = { items: AdminChatConfigVersionSummary[] };
export type AdminChatConfigVersionResponse = {
  version: AdminChatConfigVersionSummary;
  snapshot: ChatConfigSnapshot;
};

export interface ListAiChatConversationsOptions {
  cursor?: string;
  limit?: number;
}

export interface ListAiChatMessagesOptions {
  cursor?: string;
  limit?: number;
}

export type ManualSelfAnalysisResponse = {
  task_id: string;
  status: string;
  media_complete: boolean;
};

export class ApiError extends Error {
  constructor(
    public code: string,
    public status: number,
    public requestId?: string,
    public details?: {
      required_points?: number;
      available_points?: number;
      current_cursor?: number;
    },
  ) {
    super(`${code} (${status})`);
  }
}

function parseApiResponse<T>(schema: ZodType<T>, response: unknown): T {
  const parsed = schema.safeParse(response);
  if (!parsed.success) throw new ApiError('invalid_response', 502);
  return parsed.data;
}

const userAiAttachmentCapabilitySchema = z
  .object({
    supported: z.boolean(),
    max_bytes: z.number(),
    max_duration_ms: z.number().nullable(),
  })
  .strict();

const userAiAttachmentCapabilitiesSchema = z
  .object({
    max_count: z.number(),
    max_total_bytes: z.number(),
    kinds: z
      .object({
        image: userAiAttachmentCapabilitySchema,
        pdf: userAiAttachmentCapabilitySchema,
        office: userAiAttachmentCapabilitySchema,
        text: userAiAttachmentCapabilitySchema,
        code: userAiAttachmentCapabilitySchema,
        audio: userAiAttachmentCapabilitySchema,
        video: userAiAttachmentCapabilitySchema,
      })
      .strict(),
  })
  .strict();

const userAiModelsResponseSchema = z
  .object({
    can_manage_defaults: z.boolean(),
    features: z.array(
      z
        .object({
          feature_key: z.enum([
            'aigc_generation',
            'competitor_insight',
            'self_note_analysis',
            'chat_completion',
          ]),
          display_name: z.string(),
          tenant_default_model_id: z.string().nullable(),
          tenant_default_status: z.enum(['unset', 'valid', 'invalid']),
          recommended_model_id: z.string(),
          models: z.array(
            z
              .object({
                id: z.string(),
                display_name: z.string(),
                estimated_points: z.number(),
                supports: z
                  .object({
                    vision: z.boolean(),
                    tool: z.boolean(),
                    audio: z.boolean(),
                    image_generation: z.boolean(),
                    audio_output: z.boolean(),
                    chat_attachments: userAiAttachmentCapabilitiesSchema,
                  })
                  .strict(),
                is_recommended: z.boolean(),
                is_tenant_default: z.boolean(),
              })
              .strict(),
          ),
        })
        .strict(),
    ),
  })
  .strict();

export class ApiClient {
  constructor(private readonly options: ApiClientOptions) {}

  googleStartUrl(): string {
    return `${this.options.baseUrl}/v1/auth/google/start`;
  }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = (await this.options.getToken?.()) ?? null;
    const headers = new Headers(init.headers);
    headers.set('content-type', 'application/json');
    if (token) headers.set('authorization', `Bearer ${token}`);

    const res = await fetch(`${this.options.baseUrl}${path}`, { ...init, headers });
    return this.readResponse<T>(res);
  }

  private async readResponse<T>(res: Response): Promise<T> {
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const parsed = ApiErrorBody.safeParse(body);
      if (parsed.success) {
        const details = parsed.data.error.details;
        const cursorConflict = AiChatCursorConflictDetailsSchema.safeParse(details);
        const apiDetails = details
          ? {
              ...(typeof details.required_points === 'number'
                ? { required_points: details.required_points }
                : {}),
              ...(typeof details.available_points === 'number'
                ? { available_points: details.available_points }
                : {}),
              ...(cursorConflict.success
                ? { current_cursor: cursorConflict.data.current_cursor }
                : {}),
            }
          : undefined;
        const error = new ApiError(
          parsed.data.error.code,
          res.status,
          parsed.data.error.request_id,
          apiDetails && Object.keys(apiDetails).length > 0 ? apiDetails : undefined,
        );
        if (res.status === 401 && parsed.data.error.code === 'auth.unauthorized') {
          await this.options.onUnauthorized?.(error);
        }
        throw error;
      }
      throw new ApiError('unknown_error', res.status);
    }
    return (await res.json()) as T;
  }

  login(body: AuthRequest): Promise<AuthSessionResponse> {
    return this.request<AuthSessionResponse>('/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async createExtensionConnectCode(): Promise<ExtensionConnectCodeResponse> {
    const parsed = ExtensionConnectCodeResponse.safeParse(
      await this.request<unknown>('/v1/auth/extension-connect/code', { method: 'POST' }),
    );
    if (!parsed.success) throw new ApiError('invalid_response', 502);
    return parsed.data;
  }

  async logout(): Promise<UserLogoutResponse> {
    const parsed = UserLogoutResponse.safeParse(
      await this.request<unknown>('/v1/auth/logout', { method: 'POST' }),
    );
    if (!parsed.success) throw new ApiError('invalid_response', 502);
    return parsed.data;
  }

  register(body: AuthRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>('/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  verifyEmail(body: VerifyEmailRequest): Promise<AuthSessionResponse> {
    return this.request<AuthSessionResponse>('/v1/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  resendCode(body: ResendCodeRequest): Promise<ResendCodeResponse> {
    return this.request<ResendCodeResponse>('/v1/auth/resend-code', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  startPasswordReset(body: ResendCodeRequest): Promise<ResendCodeResponse> {
    return this.request<ResendCodeResponse>('/v1/auth/password-reset/start', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  confirmPasswordReset(body: PasswordResetConfirmRequest): Promise<ResendCodeResponse> {
    return this.request<ResendCodeResponse>('/v1/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  publicConfig(): Promise<PublicConfigResponse> {
    return this.request<PublicConfigResponse>('/v1/public/config');
  }

  pluginStatus(): Promise<PluginStatusResponse> {
    return this.request<PluginStatusResponse>('/v1/plugin-status');
  }

  notificationUnreadCount(): Promise<NotificationUnreadCountResponse> {
    return this.request<NotificationUnreadCountResponse>('/v1/notifications/unread-count');
  }

  listNotifications(options: ListNotificationsOptions = {}): Promise<NotificationListResponse> {
    const params = new URLSearchParams();
    if (options.status) params.set('status', options.status);
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request<NotificationListResponse>(`/v1/notifications${query ? `?${query}` : ''}`);
  }

  markNotificationRead(notificationId: string): Promise<NotificationReadResponse> {
    return this.request<NotificationReadResponse>(
      `/v1/notifications/${encodeURIComponent(notificationId)}/read`,
      { method: 'POST' },
    );
  }

  markAllNotificationsRead(): Promise<NotificationReadResponse> {
    return this.request<NotificationReadResponse>('/v1/notifications/read-all', { method: 'POST' });
  }

  previewNotificationTarget(target: NotificationTarget): Promise<AdminNotificationPreviewResponse> {
    return this.request<AdminNotificationPreviewResponse>('/admin/v1/notifications/preview', {
      method: 'POST',
      body: JSON.stringify({ target }),
    });
  }

  publishNotification(
    body: AdminNotificationPublishRequest,
  ): Promise<{ publication_id: string; status: 'queued'; recipient_count: number }> {
    return this.request('/admin/v1/notifications/publications', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  listNotificationPublications(
    options: ListNotificationPublicationsOptions = {},
  ): Promise<NotificationPublicationsResponse> {
    const params = new URLSearchParams();
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request<NotificationPublicationsResponse>(
      `/admin/v1/notifications/publications${query ? `?${query}` : ''}`,
    );
  }

  adminChatConfigDraft(): Promise<AdminChatConfigDraftResponse> {
    return this.request<AdminChatConfigDraftResponse>('/admin/v1/chat-config/draft');
  }

  updateAdminChatConfigDraft(
    body: AdminChatConfigDraftUpdateRequest,
  ): Promise<AdminChatConfigDraftResponse> {
    return this.request<AdminChatConfigDraftResponse>('/admin/v1/chat-config/draft', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  listAdminChatConfigSkills(
    options: ListAdminChatConfigSkillsOptions = {},
  ): Promise<AdminChatSkillsListResponse> {
    const params = new URLSearchParams();
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.q) params.set('q', options.q);
    if (options.status) params.set('status', options.status);
    const query = params.toString();
    return this.request<AdminChatSkillsListResponse>(
      `/admin/v1/chat-config/skills${query ? `?${query}` : ''}`,
    );
  }

  adminChatConfigSkill(skillId: string): Promise<AdminChatSkillDetailResponse> {
    return this.request<AdminChatSkillDetailResponse>(
      `/admin/v1/chat-config/skills/${encodeURIComponent(skillId)}`,
    );
  }

  createAdminChatConfigSkill(
    body: AdminChatSkillCreateRequest,
  ): Promise<AdminChatConfigSkillMutationResponse> {
    return this.request<AdminChatConfigSkillMutationResponse>('/admin/v1/chat-config/skills', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateAdminChatConfigSkill(
    skillId: string,
    body: AdminChatSkillUpdateRequest,
  ): Promise<AdminChatConfigSkillMutationResponse> {
    return this.request<AdminChatConfigSkillMutationResponse>(
      `/admin/v1/chat-config/skills/${encodeURIComponent(skillId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
  }

  setAdminChatConfigSkillStatus(
    skillId: string,
    body: AdminChatConfigSkillStatusRequest,
  ): Promise<AdminChatConfigSkillMutationResponse> {
    return this.request<AdminChatConfigSkillMutationResponse>(
      `/admin/v1/chat-config/skills/${encodeURIComponent(skillId)}/status`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
  }

  archiveAdminChatConfigSkill(
    skillId: string,
    body: AdminChatConfigSkillArchiveRequest,
  ): Promise<AdminChatConfigSkillArchiveMutationResponse> {
    return this.request<AdminChatConfigSkillArchiveMutationResponse>(
      `/admin/v1/chat-config/skills/${encodeURIComponent(skillId)}`,
      { method: 'DELETE', body: JSON.stringify(body) },
    );
  }

  reorderAdminChatConfigSkills(
    body: AdminChatConfigSkillOrderRequest,
  ): Promise<AdminChatConfigDraftResponse> {
    return this.request<AdminChatConfigDraftResponse>('/admin/v1/chat-config/skills/order', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  publishAdminChatConfig(
    body: AdminChatConfigPublishRequest,
  ): Promise<AdminChatConfigOperationResponse> {
    return this.request<AdminChatConfigOperationResponse>('/admin/v1/chat-config/publish', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  listAdminChatConfigVersions(
    options: ListAdminChatConfigVersionsOptions = {},
  ): Promise<AdminChatConfigVersionsResponse> {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request<AdminChatConfigVersionsResponse>(
      `/admin/v1/chat-config/versions${query ? `?${query}` : ''}`,
    );
  }

  adminChatConfigVersion(versionId: string): Promise<AdminChatConfigVersionResponse> {
    return this.request<AdminChatConfigVersionResponse>(
      `/admin/v1/chat-config/versions/${encodeURIComponent(versionId)}`,
    );
  }

  republishAdminChatConfigVersion(
    versionId: string,
    body: AdminChatConfigRepublishRequest,
  ): Promise<AdminChatConfigOperationResponse> {
    return this.request<AdminChatConfigOperationResponse>(
      `/admin/v1/chat-config/versions/${encodeURIComponent(versionId)}/republish`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  inspectAdminChatConfigCache(): Promise<AdminChatConfigCacheInspection> {
    return this.request<AdminChatConfigCacheInspection>('/admin/v1/chat-config/cache/inspect');
  }

  rebuildAdminChatConfigCache(
    body: AdminChatConfigCacheRebuildRequest,
  ): Promise<AdminChatConfigOperationResponse> {
    return this.request<AdminChatConfigOperationResponse>('/admin/v1/chat-config/cache/rebuild', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  createAccountBindingChallenge(
    body: AccountBindingChallengeRequest,
  ): Promise<AccountBindingChallengeResponse> {
    return this.request<AccountBindingChallengeResponse>('/v1/accounts/binding-challenges', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  confirmAccountBinding(
    body: AccountBindingConfirmRequest,
  ): Promise<AccountBindingConfirmResponse> {
    return this.request<AccountBindingConfirmResponse>('/v1/accounts/binding-confirm', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  accountsStatus(platform: Platform): Promise<AccountsStatusResponse> {
    return this.request<AccountsStatusResponse>(
      `/v1/accounts/status?platform=${encodeURIComponent(platform)}`,
    );
  }

  accountDiagnosis(accountId: string, platform: Platform): Promise<AccountDiagnosisResponse> {
    return this.request<AccountDiagnosisResponse>(
      `/v1/accounts/${encodeURIComponent(accountId)}/diagnosis?platform=${encodeURIComponent(platform)}`,
    );
  }

  accountDailyMetrics(
    accountId: string,
    platform: Platform,
    options: AccountDailyMetricsOptions = {},
  ): Promise<AccountDailyMetricsResponse> {
    const params = new URLSearchParams({ platform });
    if (options.from) params.set('from', options.from);
    if (options.to) params.set('to', options.to);
    return this.request<AccountDailyMetricsResponse>(
      `/v1/accounts/${encodeURIComponent(accountId)}/daily-metrics?${params.toString()}`,
    );
  }

  settings(): Promise<SettingsResponse> {
    return this.request<SettingsResponse>('/v1/settings');
  }

  aiPointLedger(options: AiPointLedgerQueryInput = {}): Promise<AiPointLedgerResponse> {
    const params = new URLSearchParams();
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.from) params.set('from', options.from);
    if (options.to) params.set('to', options.to);
    if (options.limit != null) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request(`/v1/ai-points/ledger${query ? `?${query}` : ''}`);
  }

  plans(): Promise<PlanCatalogResponse> {
    return this.request<PlanCatalogResponse>('/v1/plans');
  }

  async publicPlans(): Promise<PublicPlansResponse> {
    const body = await this.request<unknown>('/v1/public/plans');
    return PublicPlansResponse.parse(body);
  }

  platforms(): Promise<PlatformCatalogResponse> {
    return this.request<PlatformCatalogResponse>('/v1/platforms');
  }

  dailyRecommendation(platform: Platform): Promise<DailyRecommendationResponse> {
    return this.request<DailyRecommendationResponse>(
      `/v1/daily-recommendation?platform=${encodeURIComponent(platform)}`,
    );
  }

  refreshDailyRecommendation(
    platform: Platform,
    allowLowData = false,
  ): Promise<DailyRecommendationResponse> {
    return this.request<DailyRecommendationResponse>('/v1/daily-recommendation/refresh', {
      method: 'POST',
      body: JSON.stringify({ platform, allow_low_data: allowLowData }),
    });
  }

  sendDailyRecommendationFeedback(
    groupId: string,
    body: DailyRecommendationFeedbackRequest,
  ): Promise<{ ok: true }> {
    return this.request<{ ok: true }>(
      `/v1/daily-recommendation/${encodeURIComponent(groupId)}/feedback`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  updateSettings(body: SettingsUpdateRequest): Promise<SettingsResponse> {
    return this.request<SettingsResponse>('/v1/settings', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  listNotes(options: ListNotesOptions = {}): Promise<NotesListResponse> {
    const params = new URLSearchParams();
    if (options.platform) params.set('platform', options.platform);
    if (options.accountId) params.set('account_id', options.accountId);
    if (options.crawlPurpose) params.set('crawl_purpose', options.crawlPurpose);
    if (options.createdAfter) params.set('created_after', options.createdAfter);
    if (options.createdBefore) params.set('created_before', options.createdBefore);
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request<NotesListResponse>(`/v1/notes${query ? `?${query}` : ''}`);
  }

  requestCompetitorInsight(
    noteId: string,
    body: CompetitorInsightRequest,
  ): Promise<CompetitorInsightRequestResponse> {
    return this.request<CompetitorInsightRequestResponse>(
      `/v1/notes/${encodeURIComponent(noteId)}/competitor-insight`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  competitorInsight(noteId: string): Promise<CompetitorInsightDetailResponse> {
    return this.request<CompetitorInsightDetailResponse>(
      `/v1/notes/${encodeURIComponent(noteId)}/competitor-insight`,
    );
  }

  async userAiModels(): Promise<UserAiModelsResponse> {
    const response = await this.request<unknown>('/v1/ai/models');
    return parseApiResponse(userAiModelsResponseSchema, response);
  }

  async chatCompletion(body: AiChatCompletionRequest): Promise<AiChatGenerationAcceptedResponse> {
    const response = await this.request<unknown>('/v1/ai/chat/messages', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return parseApiResponse(AiChatGenerationAcceptedResponseSchema, response);
  }

  async chatGeneration(
    generationId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<AiChatCompletionResponse> {
    const response = await this.request<unknown>(
      `/v1/ai/chat/generations/${encodeURIComponent(generationId)}`,
      { signal: options.signal },
    );
    return parseApiResponse(AiChatCompletionResponseSchema, response);
  }

  async *streamChatGeneration(
    generationId: string,
    options: { cursor?: number; signal?: AbortSignal } = {},
  ): AsyncGenerator<AiChatStreamEvent> {
    const cursor = options.cursor ?? 0;
    if (!Number.isSafeInteger(cursor) || cursor < 0) {
      throw new ApiError('chat.invalid_cursor', 400);
    }
    const token = (await this.options.getToken?.()) ?? null;
    const headers = new Headers({ accept: 'text/event-stream' });
    if (token) headers.set('authorization', `Bearer ${token}`);
    if (cursor > 0) headers.set('last-event-id', String(cursor));
    const response = await fetch(
      `${this.options.baseUrl}/v1/ai/chat/generations/${encodeURIComponent(generationId)}/events?cursor=${cursor}`,
      { headers, signal: options.signal },
    );
    if (!response.ok) {
      await this.readResponse<never>(response);
      return;
    }
    if (!response.body) throw new ApiError('chat.stream_unavailable', 503);
    for await (const data of parseEventStreamData(response.body)) {
      yield AiChatStreamEventSchema.parse(JSON.parse(data));
    }
  }

  async chatQuote(body: AiChatQuoteRequest): Promise<AiChatQuoteResponse> {
    const response = await this.request<unknown>('/v1/ai/chat/quote', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return parseApiResponse(AiChatQuoteResponseSchema, response);
  }

  async createChatAttachment(
    body: AiChatAttachmentCreateRequest,
  ): Promise<AiChatAttachmentCreateResponse> {
    const response = await this.request<unknown>('/v1/ai/chat/attachments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return parseApiResponse(AiChatAttachmentCreateResponseSchema, response);
  }

  async uploadChatAttachment(
    attachmentId: string,
    body: Blob | ArrayBuffer,
    contentType?: string,
  ): Promise<AiChatAttachmentUploadResponse> {
    const resolvedContentType = contentType ?? (body instanceof Blob ? body.type : '');
    if (!resolvedContentType.trim()) {
      throw new ApiError('chat.attachment_invalid_input', 400);
    }
    const token = (await this.options.getToken?.()) ?? null;
    const headers = new Headers({ 'content-type': resolvedContentType });
    if (token) headers.set('authorization', `Bearer ${token}`);
    const response = await fetch(
      `${this.options.baseUrl}/v1/ai/chat/attachments/${encodeURIComponent(attachmentId)}/content`,
      { method: 'PUT', headers, body },
    );
    const payload = await this.readResponse<unknown>(response);
    return parseApiResponse(AiChatAttachmentUploadResponseSchema, payload);
  }

  async deleteChatAttachment(attachmentId: string): Promise<AiChatAttachmentDeleteResponse> {
    const response = await this.request<unknown>(
      `/v1/ai/chat/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    );
    return parseApiResponse(AiChatAttachmentDeleteResponseSchema, response);
  }

  async listChatConversations(
    options: ListAiChatConversationsOptions = {},
  ): Promise<AiChatConversationListResponse> {
    const params = new URLSearchParams();
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    const response = await this.request<unknown>(
      `/v1/ai/chat/conversations${query ? `?${query}` : ''}`,
    );
    return parseApiResponse(AiChatConversationListResponseSchema, response);
  }

  async listChatMessages(
    conversationId: string,
    options: ListAiChatMessagesOptions = {},
  ): Promise<AiChatMessageListResponse> {
    const params = new URLSearchParams();
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    const response = await this.request<unknown>(
      `/v1/ai/chat/conversations/${encodeURIComponent(conversationId)}/messages${query ? `?${query}` : ''}`,
    );
    return parseApiResponse(AiChatMessageListResponseSchema, response);
  }

  async renameChatConversation(
    conversationId: string,
    body: AiChatConversationRenameRequest,
  ): Promise<AiChatConversation> {
    const response = await this.request<unknown>(
      `/v1/ai/chat/conversations/${encodeURIComponent(conversationId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
    return parseApiResponse(AiChatConversationSchema, response);
  }

  async deleteChatConversation(conversationId: string): Promise<AiChatConversationDeleteResponse> {
    const response = await this.request<unknown>(
      `/v1/ai/chat/conversations/${encodeURIComponent(conversationId)}`,
      { method: 'DELETE' },
    );
    return parseApiResponse(AiChatConversationDeleteResponseSchema, response);
  }

  noteDetail(noteId: string): Promise<NoteDetailResponse> {
    return this.request<NoteDetailResponse>(`/v1/notes/${encodeURIComponent(noteId)}`);
  }

  startAccountSync(accountId: string, platform: Platform): Promise<AccountSyncRun> {
    return this.request<AccountSyncRun>(
      `/v1/accounts/${encodeURIComponent(accountId)}/sync-runs?platform=${encodeURIComponent(platform)}`,
      { method: 'POST' },
    );
  }

  continueAccountSync(
    runId: string,
    accountId: string,
    bindingVersion: number,
  ): Promise<{ ok: true }> {
    return this.request(`/v1/account-sync-runs/${encodeURIComponent(runId)}/continue`, {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId, account_binding_version: bindingVersion }),
    });
  }

  requestSelfAnalysis(
    noteId: string,
    body: ManualSelfAnalysisRequest,
  ): Promise<ManualSelfAnalysisResponse> {
    return this.request(`/v1/notes/${encodeURIComponent(noteId)}/self-analysis`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  retryNoteDetail(noteId: string): Promise<{ ok: true; sync_run_id: string }> {
    return this.request(`/v1/notes/${encodeURIComponent(noteId)}/retry-detail`, {
      method: 'POST',
    });
  }

  listCreators(options: ListCreatorsOptions = {}): Promise<CreatorsListResponse> {
    const params = new URLSearchParams();
    if (options.platform) params.set('platform', options.platform);
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request<CreatorsListResponse>(`/v1/creators${query ? `?${query}` : ''}`);
  }

  listSkills(options: ListSkillsOptions): Promise<SkillsListResponse> {
    const params = new URLSearchParams();
    params.set('platform', options.platform);
    if (options.layer) params.set('layer', String(options.layer));
    if (options.sourceType) params.set('source_type', options.sourceType);
    if (options.category) params.set('category', options.category);
    if (options.limit) params.set('limit', String(options.limit));
    return this.request<SkillsListResponse>(`/v1/skills?${params.toString()}`);
  }

  skillExtractionReadiness(platform: Platform): Promise<SkillExtractionReadinessResponse> {
    const params = new URLSearchParams({ platform });
    return this.request<SkillExtractionReadinessResponse>(
      `/v1/skills/extraction?${params.toString()}`,
    );
  }

  startSkillExtraction(body: SkillExtractionStartRequest): Promise<SkillExtractionStartResponse> {
    return this.request<SkillExtractionStartResponse>('/v1/skills/extraction', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  readSkill(skillId: string): Promise<SkillReadResponse> {
    return this.request<SkillReadResponse>(`/v1/skills/${encodeURIComponent(skillId)}`);
  }

  createSkill(body: SkillCreateRequest): Promise<SkillCreateResponse> {
    return this.request<SkillCreateResponse>('/v1/skills', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  manageSkill(skillId: string): Promise<SkillManageResponse> {
    return this.request<SkillManageResponse>(`/v1/skills/${encodeURIComponent(skillId)}/manage`);
  }

  updateSkill(skillId: string, body: SkillUpdateRequest): Promise<SkillUpdateResponse> {
    return this.request<SkillUpdateResponse>(`/v1/skills/${encodeURIComponent(skillId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  archiveSkill(skillId: string): Promise<SkillArchiveResponse> {
    return this.request<SkillArchiveResponse>(`/v1/skills/${encodeURIComponent(skillId)}`, {
      method: 'DELETE',
    });
  }

  selectSkills(body: SkillSelectRequest): Promise<SkillSelectResponse> {
    return this.request<SkillSelectResponse>('/v1/skills/select', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  listContentDrafts(options: ListContentDraftsOptions = {}): Promise<ContentDraftsListResponse> {
    const params = new URLSearchParams();
    if (options.platform) params.set('platform', options.platform);
    if (options.q) params.set('q', options.q);
    if (options.status) params.set('status', options.status);
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request<ContentDraftsListResponse>(`/v1/content-drafts${query ? `?${query}` : ''}`);
  }

  generateDraft(body: GenerateDraftRequest): Promise<GenerateDraftResponse> {
    return this.request<GenerateDraftResponse>('/v1/content-drafts/generate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  generateDraftVariants(
    body: GenerateDraftVariantsRequest,
  ): Promise<GenerateDraftVariantsResponse> {
    return this.request<GenerateDraftVariantsResponse>('/v1/content-drafts/generate-variants', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async submitContentGenerationBatch(
    body: ContentGenerationBatchSubmitRequest,
  ): Promise<ContentGenerationBatchResponse> {
    const response = await this.request<unknown>('/v1/content-generation-batches', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const parsed = ContentGenerationBatchResponseBody.safeParse(response);
    if (!parsed.success) throw new ApiError('invalid_response', 502);
    return parsed.data;
  }

  async latestContentGenerationBatch(
    options: ReadLatestContentGenerationBatchOptions,
  ): Promise<{ batch: ContentGenerationBatchResponse['batch'] | null }> {
    const params = new URLSearchParams({ platform: options.platform });
    const response = await this.request<unknown>(
      `/v1/content-generation-batches/latest?${params.toString()}`,
    );
    const parsed = LatestContentGenerationBatchResponseBody.safeParse(response);
    if (!parsed.success) throw new ApiError('invalid_response', 502);
    return parsed.data;
  }

  async readContentGenerationBatch(batchId: string): Promise<ContentGenerationBatchResponse> {
    const response = await this.request<unknown>(
      `/v1/content-generation-batches/${encodeURIComponent(batchId)}`,
    );
    const parsed = ContentGenerationBatchResponseBody.safeParse(response);
    if (!parsed.success) throw new ApiError('invalid_response', 502);
    return parsed.data;
  }

  listSavedTopics(options: ListSavedTopicsOptions = {}): Promise<SavedTopicsListResponse> {
    const params = new URLSearchParams();
    if (options.platform) params.set('platform', options.platform);
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request<SavedTopicsListResponse>(`/v1/saved-topics${query ? `?${query}` : ''}`);
  }

  saveTopic(body: SavedTopicCreateRequest): Promise<SavedTopicCreateResponse> {
    return this.request<SavedTopicCreateResponse>('/v1/saved-topics', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  deleteSavedTopic(topicId: string): Promise<SavedTopicDeleteResponse> {
    return this.request<SavedTopicDeleteResponse>(
      `/v1/saved-topics/${encodeURIComponent(topicId)}`,
      {
        method: 'DELETE',
      },
    );
  }

  readContentDraft(draftId: string): Promise<ContentDraftReadResponse> {
    return this.request<ContentDraftReadResponse>(
      `/v1/content-drafts/${encodeURIComponent(draftId)}`,
    );
  }

  async readContentDraftGenerationBatch(
    draftId: string,
  ): Promise<ContentDraftGenerationBatchReadResponse> {
    const response = await this.request<unknown>(
      `/v1/content-drafts/${encodeURIComponent(draftId)}/generation-batch`,
    );
    const parsed = ContentDraftGenerationBatchReadResponseBody.safeParse(response);
    if (!parsed.success) throw new ApiError('invalid_response', 502);
    return parsed.data;
  }

  readContentDraftVersion(
    draftId: string,
    versionId: string,
  ): Promise<ContentDraftVersionReadResponse> {
    return this.request<ContentDraftVersionReadResponse>(
      `/v1/content-drafts/${encodeURIComponent(draftId)}/versions/${encodeURIComponent(versionId)}`,
    );
  }

  updateContentDraft(
    draftId: string,
    body: ContentDraftUpdateRequest,
  ): Promise<ContentDraftUpdateResponse> {
    return this.request<ContentDraftUpdateResponse>(
      `/v1/content-drafts/${encodeURIComponent(draftId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
    );
  }

  rewriteContentDraft(
    draftId: string,
    body: RewriteContentDraftRequest,
  ): Promise<RewriteContentDraftResponse> {
    return this.request<RewriteContentDraftResponse>(
      `/v1/content-drafts/${encodeURIComponent(draftId)}/rewrite`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  }

  archiveContentDraft(draftId: string): Promise<ContentDraftArchiveResponse> {
    return this.request<ContentDraftArchiveResponse>(
      `/v1/content-drafts/${encodeURIComponent(draftId)}`,
      {
        method: 'DELETE',
      },
    );
  }

  deleteContentDraft(draftId: string): Promise<ContentDraftDeleteResponse> {
    return this.request<ContentDraftDeleteResponse>(
      `/v1/content-drafts/${encodeURIComponent(draftId)}/delete`,
      {
        method: 'POST',
      },
    );
  }

  pinContentDraft(draftId: string, body: ContentDraftPinRequest): Promise<ContentDraftPinResponse> {
    return this.request<ContentDraftPinResponse>(
      `/v1/content-drafts/${encodeURIComponent(draftId)}/pin`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
    );
  }
}

async function* parseEventStreamData(stream: ReadableStream<Uint8Array>): AsyncGenerator<string> {
  const reader = stream.getReader();
  const decoder = new TextDecoder('utf-8', { fatal: true, ignoreBOM: false });
  const encoder = new TextEncoder();
  let buffer = '';
  let totalBytes = 0;
  let eventBytes = 0;
  let dataLines: string[] = [];
  const dispatch = (): string | null => {
    if (dataLines.length === 0) {
      eventBytes = 0;
      return null;
    }
    const data = dataLines.join('\n');
    dataLines = [];
    eventBytes = 0;
    return data;
  };
  const consumeLine = (line: string): string | null => {
    const bytes = encoder.encode(line).byteLength;
    if (bytes > 128 * 1024) throw new ApiError('chat.stream_invalid', 502);
    eventBytes += bytes + 2;
    if (eventBytes > 256 * 1024) throw new ApiError('chat.stream_invalid', 502);
    if (line === '') return dispatch();
    if (line.startsWith(':')) return null;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'data') dataLines.push(value);
    return null;
  };
  const drain = function* (flush: boolean): Generator<string> {
    while (buffer.length > 0) {
      const boundary = eventStreamLineBoundary(buffer, flush);
      if (!boundary) break;
      const line = buffer.slice(0, boundary.index);
      buffer = buffer.slice(boundary.index + boundary.length);
      const data = consumeLine(line);
      if (data !== null) yield data;
    }
  };
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      totalBytes += value.byteLength;
      if (totalBytes > AI_CHAT_STREAM_CLIENT_MAX_BYTES) {
        throw new ApiError('chat.stream_too_large', 502);
      }
      buffer += decoder.decode(value, { stream: true });
      if (encoder.encode(buffer).byteLength > 512 * 1024) {
        throw new ApiError('chat.stream_invalid', 502);
      }
      yield* drain(false);
    }
    buffer += decoder.decode();
    yield* drain(true);
    const final = dispatch();
    if (final !== null) yield final;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('chat.stream_invalid', 502);
  } finally {
    reader.releaseLock();
  }
}

function eventStreamLineBoundary(
  value: string,
  flush: boolean,
): { index: number; length: number } | null {
  for (let index = 0; index < value.length; index += 1) {
    if (value[index] === '\n') return { index, length: 1 };
    if (value[index] === '\r') {
      if (index + 1 >= value.length && !flush) return null;
      return { index, length: value[index + 1] === '\n' ? 2 : 1 };
    }
  }
  return flush ? { index: value.length, length: 0 } : null;
}
